import React from 'react';

import { useQuery } from '@apollo/client/react';
import { c, msgid, ngettext } from 'ttag';

import { Button } from '@freelancelabs/components';

import {
    AccessibleAccountsDocument,
    AssignablePartnerListsDocument,
    RolesDocument,
} from '../../../graphql/__generated__/types';
import { EMPTY } from '../display';
import ReviewAttachment from './ReviewAttachment';
import { contactFullName, contactStatusReading } from './contactLookup';
import { Draft } from './draft';
import { findRole, roleRefusal } from './grantableRoles';

interface Props {
    draft: Draft;
    onEditStep: (step: number) => void;
}

/** Step 5: what is about to be created. Going back is not one-way, and the perimeter uses the same helper as the user sheet. */
const ReviewStep = ({ draft, onEditStep }: Props) => {
    const { data: accountsData } = useQuery(AccessibleAccountsDocument);
    const { data: rolesData } = useQuery(RolesDocument);

    const accountName = (organizationId: string) =>
        (accountsData?.accessibleAccounts ?? []).find((account) => account?.id === organizationId)?.name ??
        organizationId;
    const roles = (rolesData?.portal ?? []).filter(Boolean);
    const roleName = (roleId: string | null) => findRole(roles, roleId)?.name ?? EMPTY;

    // Why this line cannot be sent, or null. Repeated from the footer because that names the role and this the account.
    const refusalOf = (roleId: string | null) => {
        const role = findRole(roles, roleId);
        return role?.grantableByViewer === false ? roleRefusal(role.notGrantableReason).tag : null;
    };

    // Read from cache: the recap names the list, falling back to the id rather than a blank.
    const { data: partnerListsData } = useQuery(AssignablePartnerListsDocument);
    const partnerListName =
        (partnerListsData?.assignablePartnerLists?.lists ?? []).find((list) => list?.id === draft.partnerListId)
            ?.name ?? draft.partnerListId;

    const dashboardCount = draft.dashboards.length;
    // The identity as the source writes it. No fallback to a blank: a missing name means no contact
    // is attached, which is what blocks the send.
    const contact = draft.contact;
    const fullName = contact ? (contactFullName(contact) ?? '') : '';
    const statusReading = contact ? contactStatusReading(contact.onboardingStatus) : null;
    // Resolved outside the template: ttag refuses a call expression inside a localised string.
    const contactReference = contact?.businessReference ?? EMPTY;

    return (
        <div className="flex flex-col gap-4">
            <section className="rounded-lg border border-border-norm p-4">
                <div className="flex flex-wrap items-start gap-3">
                    <div>
                        <h3 className="text-sm font-semibold text-text-900">{c('Onboarding recap')
                            .t`Identification`}</h3>
                        <p className="mt-1 font-mono text-sm">{draft.email}</p>
                        {contact ? (
                            <>
                                <p className="text-xs text-text-600">
                                    {fullName}
                                    {contact.jobFunction ? `, ${contact.jobFunction}` : ''}
                                </p>
                                <p className="mt-1 text-xs text-text-600">
                                    {c('Onboarding recap')
                                        .t`Identity read from the source — reference ${contactReference}.`}
                                </p>
                                {/* "Already onboarded" changes what the send does, so it is said here; it is not a refusal. */}
                                {statusReading && !statusReading.selectable && (
                                    <p className="mt-1 text-sm text-danger-600" role="alert">
                                        {statusReading.meaning}
                                    </p>
                                )}
                            </>
                        ) : (
                            <p className="text-sm text-danger-600" role="alert">
                                {c('Onboarding recap')
                                    .t`No contact is attached to this address any more — it changed after one was picked. Go back to step 1 and search it again: the invitation cannot be sent without an identity from the source.`}
                            </p>
                        )}
                    </div>
                    <Button className="ml-auto" size="small" category="weak" onClick={() => onEditStep(1)}>
                        {c('Onboarding action').t`Edit`}
                    </Button>
                </div>
            </section>

            <section className="rounded-lg border border-border-norm p-4">
                <div className="flex flex-wrap items-start gap-3">
                    <h3 className="text-sm font-semibold text-text-900">
                        {c('Onboarding recap').t`Accounts, roles and perimeters`}
                    </h3>
                    <Button className="ml-auto" size="small" category="weak" onClick={() => onEditStep(2)}>
                        {c('Onboarding action').t`Edit`}
                    </Button>
                </div>
                <ul className="mt-3 flex flex-col gap-2">
                    {draft.attachments.map((attachment) => (
                        <ReviewAttachment
                            key={attachment.organizationId}
                            attachment={attachment}
                            accountName={accountName(attachment.organizationId)}
                            roleName={roleName(attachment.roleId)}
                            refusal={refusalOf(attachment.roleId)}
                            email={draft.contact?.email ?? ''}
                        />
                    ))}
                </ul>
            </section>

            <section className="rounded-lg border border-border-norm p-4">
                <div className="flex flex-wrap items-start gap-3">
                    <h3 className="text-sm font-semibold text-text-900">{c('Onboarding recap').t`Dashboards`}</h3>
                    <Button className="ml-auto" size="small" category="weak" onClick={() => onEditStep(3)}>
                        {c('Onboarding action').t`Edit`}
                    </Button>
                </div>
                {draft.dashboards.length === 0 ? (
                    <p className="mt-2 text-xs text-text-600">
                        {c('Onboarding recap')
                            .t`No dashboard granted — the « Dashboard » module will stay hidden in this person's portal.`}
                    </p>
                ) : (
                    <p className="mt-2 text-xs text-text-600">
                        {ngettext(
                            msgid`${dashboardCount} dashboard granted.`,
                            `${dashboardCount} dashboards granted.`,
                            dashboardCount
                        )}
                    </p>
                )}
            </section>

            <section className="rounded-lg border border-border-norm p-4">
                <div className="flex flex-wrap items-start gap-3">
                    <h3 className="text-sm font-semibold text-text-900">{c('Onboarding recap').t`Partners`}</h3>
                    <Button className="ml-auto" size="small" category="weak" onClick={() => onEditStep(4)}>
                        {c('Onboarding action').t`Edit`}
                    </Button>
                </div>
                <p className="mt-2 text-xs text-text-600">
                    {draft.partnerListId
                        ? partnerListName
                        : c('Onboarding recap')
                              .t`No list associated — the « Partenaires » module will stay empty for this person.`}
                </p>
            </section>
        </div>
    );
};

export default ReviewStep;
